import { AttachmentPreview } from "@/app/Threads/Components/AttachmentPreview";
import { type ThreadMessageFragment } from "@/app/Threads/GraphQL/threadMessageQueries";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { cn } from "@/lib/utils";
import { ThreadUserAvatar } from "@/app/Threads/Components/ThreadUserAvatar";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from "use-intl";
import dayjs from "dayjs";

type Props = {
  message: ThreadMessageFragment;
};

export function MessageBubble({ message }: Props) {
  const t = useTranslations("thread.notes");
  const { activeOrganisation } = useActiveOrganisation();
  const currentOrgId = activeOrganisation?.id ?? null;
  const isNote = message.is_admin_note;
  const isMine =
    message.organisation_id != null &&
    String(message.organisation_id) === String(currentOrgId);

  return (
    <div
      className={cn(
        "flex items-end gap-2",
        isMine ? "flex-row-reverse justify-start" : "flex-row justify-start",
      )}
    >
      <UserAvatar message={message} />
      <div
        className={cn(
          "flex max-w-md flex-col gap-1",
          isMine ? "items-end" : "items-start",
        )}
      >
        <div className="flex items-center gap-1.5">
          {isNote ? (
            <Tooltip>
              <TooltipTrigger
                type="button"
                aria-label={t("label")}
                className="rounded-full bg-amber-200 p-0.5 text-amber-900 hover:bg-amber-300"
              >
                <LockClosedIcon className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent>{t("label")}</TooltipContent>
            </Tooltip>
          ) : null}
          <span className="text-xs font-semibold text-gray-700">
            {message.user.name}
          </span>
          <span className="text-xs text-gray-400">
            {dayjs.utc(message.created_at).local().format("h:mm A")}
          </span>
        </div>
        <div
          className={cn(
            "rounded-lg px-4 py-2",
            isNote
              ? "bg-amber-200 text-gray-900"
              : isMine
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-900",
          )}
        >
          {message.body ? (
            <p className="whitespace-pre-wrap text-sm">{message.body}</p>
          ) : null}
          {message.attachments.length > 0 ? (
            <div className="mt-1 space-y-1">
              {message.attachments.map((attachment) => (
                <AttachmentPreview
                  key={attachment.id}
                  attachment={attachment}
                  isMine={isMine && !isNote}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function UserAvatar({ message }: { message: ThreadMessageFragment }) {
  const organisation = message.organisation;

  return (
    <div className="relative shrink-0">
      <ThreadUserAvatar
        className="size-8"
        name={message.user.name}
        media={message.user.profilePicture}
      />
      {organisation ? (
        <Tooltip>
          <TooltipTrigger className="absolute -bottom-1 -right-1 flex">
            <Avatar
              className="size-4 bg-white outline -outline-offset-1 outline-black/5 ring-2 ring-white"
              media={organisation.profilePicture ?? null}
              initials={organisation.name ? getInitials(organisation.name) : ""}
            />
          </TooltipTrigger>
          <TooltipContent>{organisation.name}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}
